import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import ConfirmationModal from './containers/ConfirmationModal';
import Snackbar from './components/Snackbar';
import { ThemeProvider } from 'styled-components';
import {
    ThemeProvider as MuiThemeProvider,
    StyledEngineProvider,
} from '@mui/material/styles';
import { I18nextProvider } from 'react-i18next';
import { useStores } from './hooks/use-stores';
import MainRoutes from './MainRoutes';
import lang from 'validatorjs/src/lang';
import en from 'validatorjs/src/lang/en';
import { useLocation, useNavigate } from 'react-router-dom';

lang._set('en', en);

function App() {
    const { store, authStore, commonStore, brandingStore } = useStores();
    const location = useLocation();
    const navigate = useNavigate();
    const {isPartnerAdmin} = authStore;
    const { theme, muiTheme, brandingLoaded } = brandingStore;
    const { appLoaded, messageShown, languagesLoaded, i18n } = commonStore;


    const { isAuthenticated } = authStore;
    const {
        trialMessageBar,
        currentCompany,
        loadingData,
        assessmentDashboardEnabled,
        assessmentEnabled,
        assessmentInsightEnabled,
        phishingSimulationMenuEnabled,
        trainingEnabled,
        serverData
    } = store;

    useEffect(() => {
        const isLoginLink = location.pathname.startsWith('/auth/jwt/signin');


        (async () => {
            if (authStore.token && !isLoginLink) {
                try {
                    await Promise.all([
                        authStore.pullUser(),
                        commonStore.loadLanguages(),
                    ]);
                } finally {
                    await commonStore.setAppLoaded();
                }
            } else {
                await commonStore.loadLanguages();
                await brandingStore.loadBranding();
                await commonStore.setAppLoaded();
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            if (isPartnerAdmin && serverData?.length === 0 && !loadingData) {
                const superForAccounts = authStore.currentUser.superForAccounts || [];
                const preferred = superForAccounts[0] || null;
                if (preferred && currentCompany == null && !location.pathname.startsWith('/partner')) {
                    navigate(`/partner/${preferred.id}/companies`);
                }
                if (location.pathname.startsWith('/partner') && currentCompany) {
                    store.resetCurrentCompany();
                }

                //If on setting page, top navbar items are displayed, not sure if hide top bar items in this case (not a good option) or redirect to partner/companies or hide settings at all (because there is no company and there is only name and email in settings)
                //Now cyclic redirects are not handled, so we need to check if the user is already on the partner page OR on the company page OR generally is where he is supposed:
                //Probably the best way is to skip redirect if:
                // 1) serverData is empty (so we ensure we don't own any company),
                // 2) path start's with /partner so we are on partner page no need for redirect or
                // 3)currentCompany is not null meaning we are viewing a company
                // 4) here is important if to remove settings from the user menu or not, if we want to keep it, we need to add an exception also if we are on settings page, but here we need to decide what to do with top nav and other places where user can go through settings (look upper line)
                // in case of viewing company from partner/companies you can access companies as VIEWER not ADMIN which seems to be ok
                // when returning to partner page from a specific company (through user menu -> ... Companies) the currentCompany should be probably reset to null, beacuse of:
                // 1) branding (logo, colors remains of the company even when we are back on partner page) and
                // 2) the company name is displayed in userMenu under name which (not in the list in menu) and it is not correct, because we still don't have OUR companies
                // the last thing is case when user is partner admin to multiple companies but still don't have a company, in this case we need to somehow memorize the selected partner and redirect to it not to a [0]first parner (maybe localStorage as well?)

            }
        })();
    }, [location.pathname, isPartnerAdmin, serverData]);

    const closeSnackbar = () => {
        commonStore.hideMessage();
    };

    const handleSignout = () => {
        authStore.logout();
    };

    const primaryColor = theme.primary;

    return (
        <StyledEngineProvider injectFirst>
            <MuiThemeProvider theme={muiTheme}>
                <ThemeProvider theme={theme}>
                    {languagesLoaded && (
                        <I18nextProvider i18n={i18n}>
                            {/*{brandingLoaded && (
                                    <Route
                                        path="testing2/*"
                                        element={<TestPage />}
                                    />
                                )}
                                <Route path="infosec/*" element={<Infosec />} />

                                <Route
                                    path="auth/jwt/signin/:token"
                                    element={<TokenSignIn />}
                                />
                                <Route path=".well-known/security.txt" />*/}
                            <MainRoutes
                                {...{
                                    appLoaded,
                                    brandingLoaded,
                                    primaryColor,
                                    loadingData,
                                    handleSignout,
                                    isAuthenticated,
                                    trialMessageBar,
                                    assessmentDashboardEnabled,
                                    assessmentInsightEnabled,
                                    phishingSimulationEnabled:
                                        phishingSimulationMenuEnabled,
                                    currentCompany,
                                    trainingEnabled,
                                    assessmentEnabled,
                                }}
                            />
                            <Snackbar
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                autoHideDuration={6000}
                                variant={commonStore.messageType}
                                message={commonStore.message || ''}
                                open={messageShown}
                                onClose={closeSnackbar}
                            />
                            <ConfirmationModal />

                            {/*<Fragment>
                                <Routes>
                                    <Route
                                        path="/"
                                        render={(props) => {
                                            let normalizedPath = enforceSlash(
                                                props.location.pathname.replace(
                                                    /\/\//g,
                                                    '/'
                                                )
                                            );
                                            if (
                                                commonStore.lastPage !==
                                                normalizedPath
                                            ) {
                                                commonStore.analyticsPageView(
                                                    `${normalizedPath}`
                                                );
                                            }
                                            commonStore.lastPage = normalizedPath;
                                            return null;
                                        }}
                                    />
                                </Routes>

                                <Snackbar
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    autoHideDuration={6000}
                                    variant={commonStore.messageType}
                                    message={commonStore.message || ''}
                                    open={messageShown}
                                    onClose={closeSnackbar}
                                ></Snackbar>
                                <ConfirmationModal />
                            </Fragment>*/}
                        </I18nextProvider>
                    )}
                </ThemeProvider>
            </MuiThemeProvider>
        </StyledEngineProvider>
    );
}

export default observer(App);
